import { Injectable, inject, signal, effect, computed } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { rxResource } from "@angular/core/rxjs-interop";
import { map, of, catchError } from "rxjs";
import { Event } from "./events.service";
import { visibleEventsForPicker } from "../utils/event-filters";

@Injectable({
  providedIn: "root",
})
export class StaffEventService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = "/api/v1/events/events/";
  private readonly STORAGE_KEY = "staff_selected_event_id";

  // Resource to fetch all active events
  readonly activeEventsResource = rxResource<Event[], void>({
    stream: () => {
      console.log('[StaffEventService] Fetching all active events');
      return this.http.get<Event[]>(this.apiUrl).pipe(
        map(events => {
          // Sort: default event first, then by date descending
          return [...events].sort((a, b) => {
            if (a.is_default) return -1;
            if (b.is_default) return 1;
            return b.date_start.localeCompare(a.date_start);
          });
        })
      );
    }
  });

  getFilteredEvents(filter: 'active' | 'inactive' | 'all' = 'active') {
    return this.http.get<Event[]>(this.apiUrl, { params: { filter } }).pipe(
      map(events => {
        return [...events].sort((a, b) => {
          if (a.is_default) return -1;
          if (b.is_default) return 1;
          return b.date_start.localeCompare(a.date_start);
        });
      })
    );
  }

  deleteEvent(id: number) {
    return this.http.delete(`${this.apiUrl}${id}/`);
  }

  readonly activeEvents = computed<Event[]>(() => this.activeEventsResource.value() ?? []);

  // Staff-only preference (per browser) to hide events whose end date has passed,
  // shared between the Evenimente page switch and the navbar event picker.
  // Default is to hide past events; the preference persists in localStorage.
  private static readonly HIDE_PAST_KEY = "staff.events.hidePast";
  readonly hidePastEvents = signal<boolean>(this.readHidePastPref());

  private readHidePastPref(): boolean {
    try {
      const v = localStorage.getItem(StaffEventService.HIDE_PAST_KEY);
      return v === null ? true : v === "true";
    } catch {
      return true;
    }
  }

  setHidePastEvents(val: boolean) {
    this.hidePastEvents.set(val);
    try {
      localStorage.setItem(StaffEventService.HIDE_PAST_KEY, String(val));
    } catch {
      // localStorage unavailable (private mode) — the signal still drives the UI.
    }
  }

  // Events to show in the navbar picker: honours the hide-past preference but
  // never hides the currently-selected event, so a deliberately-selected past
  // event stays visible (and re-selectable) in the dropdown.
  readonly visibleActiveEvents = computed<Event[]>(() =>
    visibleEventsForPicker(this.activeEvents(), this.hidePastEvents(), this.selectedEventId()),
  );

  // Resource for current event from API (session-backed)
  readonly currentEventResource = rxResource<Event | null, void>({
    stream: () => {
      console.log('[StaffEventService] Fetching current event from API');
      return this.http.get<Event>(`${this.apiUrl}current_event/`).pipe(
        // If the server has no current event in session or none exist, return null instead of erroring the resource
        catchError(() => of(null))
      );
    }
  });

  // Internal signal for the selected event ID
  private readonly _selectedEventId = signal<number | null>(this.getStoredEventId());

  // Publicly exposed signal for the selected event ID
  readonly selectedEventId = this._selectedEventId.asReadonly();

  // Computed signal for the full selected event object
  readonly selectedEvent = computed<Event | null>(() => {
    const id = this.selectedEventId();
    const events = this.activeEvents();
    const apiEvent = this.currentEventResource.value();

    if (id) {
      const found = events.find(e => e.id === id);
      if (found) return found;
    }

    if (apiEvent) return apiEvent;

    return events.find(e => e.is_default) || events[0] || null;
  });

  constructor() {
    // Sync selected event to local storage whenever it changes
    effect(() => {
      const event = this.selectedEvent();
      if (event) {
        localStorage.setItem(this.STORAGE_KEY, event.id.toString());
      }
    });
  }

  initialize() {
    // This is now mostly handled by signals/resources initialization
    console.log('[StaffEventService] Initialized with ID:', this._selectedEventId());
  }

  setSelectedEventId(id: number) {
    this._selectedEventId.set(id);
    this.http.post(`${this.apiUrl}select_event/`, { event_id: id }).subscribe(() => {
      this.currentEventResource.reload();
    });
  }

  cloneEvent(templateId: number, name: string, year: number, date_start: string, date_end: string, copy_options: any) {
    return this.http.post<Event>(`${this.apiUrl}${templateId}/clone/`, {
      name,
      year,
      date_start,
      date_end,
      copy_options
    });
  }

  private getStoredEventId(): number | null {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? parseInt(stored, 10) : null;
  }
}
